import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { Text } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { useWorldStore } from '../state/worldStore';
import { interiorFloor, roomSpace } from '../state/interiorFloor';
import { seatPose, returnPose } from '../services/seats';
import { seatTake, seatLeave } from '../services/worldSocket';
import { ROOMS } from '../rooms';

// RoomMarkers — "this one, here".
//
// The world needs to be able to point at a thing. A stylist says sit down, a
// quest says take this, a tutorial says look at that — and all of them need the
// same primitive underneath: name an object, make it obviously the object.
//
// ── WHY THIS CAN WORK AT ALL ─────────────────────────────────────────────────
// Because the chair is still a chair in the shipped file. The generator's merge
// pass collapses 600 loose parts into 20 slabs by material, which is what keeps
// the draw calls sane — and it would have collapsed the chair's back, arms, base
// and footrest into a room-wide sheet of leather, leaving nothing to point at.
// Marking those groups `unit: true` (scripts/lib/kit.mjs) makes them merge
// WITHIN THEMSELVES instead, so a chair stays one addressable object with two
// draw calls. Highlighting is then just: find the group, glow its meshes.
//
// Nothing about this is salon-specific. Any authored room gets it by declaring
// its furniture as units.
//
// ── SET A MARK ───────────────────────────────────────────────────────────────
//   useWorldStore.getState().setMarks([{ target: 'wash_chair_1' }])
//   ?mark=wash_chair_1                       ← for testing without dialogue
//
// A mark is an object, not a string, because the next things it carries are
// already known: an action button anchored to it, a camera pose to move to, and
// the seat affordance to put a body on. Those hang off this without changing
// what a mark IS.
const qp = typeof window !== 'undefined'
  ? new URLSearchParams(window.location.search).get('mark')
  : null;

// Slow enough to read as breathing rather than blinking. A fast pulse says
// "error"; this has to say "come here".
const SPEED = 1.9;
// RAISED FROM 0.16–0.42, which was invisible in practice. Emissive is ADDED to
// what a surface already returns, so how much it shows depends entirely on what
// it is added to — and the first thing marked was black leather standing in the
// dimmest corner of the room. A value chosen against a pale wall disappears
// there. This range reads on the darkest material in the palette, which is the
// one that has to work.
const MIN = 0.35;
const MAX = 1.15;

// AND A RING ON THE FLOOR, because an emissive glow can always be argued with.
// It is added to a material, so a dark object in shadow can absorb almost all of
// it, and the highlight then fails exactly where a room most needs one. The ring
// is additive geometry that owes nothing to the object's material or the room's
// light: whatever else is true, there is a mark on the floor.
function Halo({ at, r, accent }) {
  const ref = useRef();
  useFrame((state) => {
    if (!ref.current) return;
    const k = (1 - Math.cos(state.clock.elapsedTime * SPEED)) / 2;
    ref.current.material.opacity = 0.25 + 0.45 * k;
    const s = 1 + 0.06 * k;
    ref.current.scale.set(s, s, 1);
  });
  return (
    <mesh ref={ref} position={at} rotation={[-Math.PI / 2, 0, 0]} renderOrder={5}>
      <ringGeometry args={[r * 0.82, r, 48]} />
      <meshBasicMaterial
        color={accent}
        transparent
        opacity={0.4}
        toneMapped={false}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </mesh>
  );
}

// THE OBJECT IS THE BUTTON (2026-10-31). Khari, on two "Sit down" boards
// hovering over a café table: "these types of signs are too large and block the
// view. We don't need to place these above the items. They can be smaller and
// overlay the object, or let the glow of the object handle the issue."
//
// So nothing floats above the furniture any more. The marked object already
// GLOWS (its own clone of its material, pulsing) and has its RING on the floor;
// a click or a tap on the object itself — an invisible box over its bounds —
// is the action. A SMALL label sits ON the object, drawn over it, and only when
// it has something to say: while you point at it (what clicking will do), while
// it is yours ("Stand up"), while it is taken or being asked for, and for a
// moment after you try one somebody else is in. A drag across it is a look, not
// a click. On a phone there is no hover: the glow says "this one", a tap sits.
// `dim` is a chair somebody else is in (a shared room, 2026-10-30): it says so
// and does nothing when pressed.
function Action({ box, label, accent, onPick, dim = false, mine = false, asking = false }) {
  const g = useRef();
  const [hot, setHot] = useState(false);
  const [flash, setFlash] = useState(0);
  const camera = useThree((s) => s.camera);
  useFrame(() => { if (g.current) g.current.quaternion.copy(camera.quaternion); });
  useEffect(() => {
    if (!flash) return undefined;
    const t = setTimeout(() => setFlash(0), 1600);
    return () => clearTimeout(t);
  }, [flash]);
  const show = hot || mine || asking || flash || (dim && hot);
  const w = Math.max(0.16, label.length * 0.026 + 0.07);
  const H = 0.068;
  const lit = hot && !dim;
  return (
    <group>
      {/* The hit area: the object's own bounds, unseen. */}
      <mesh
        position={box.center}
        onPointerOver={(e) => { e.stopPropagation(); setHot(true); document.body.style.cursor = dim ? 'not-allowed' : 'pointer'; }}
        onPointerOut={() => { setHot(false); document.body.style.cursor = ''; }}
        onClick={(e) => {
          e.stopPropagation();
          if (e.delta > 6) return;   // the end of a drag-to-look, not a click
          if (dim) { setFlash(Date.now()); return; }
          onPick();
        }}
      >
        <boxGeometry args={box.size} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} colorWrite={false} />
      </mesh>
      {show && (
        <group ref={g} position={box.label}>
          {/* Pressable too: seated, you are INSIDE your chair's hit area, so "Stand up" is pressed here. */}
          <mesh renderOrder={20} onClick={(e) => { e.stopPropagation(); if (e.delta > 6 || dim) return; onPick(); }}>
            <planeGeometry args={[w, H]} />
            <meshBasicMaterial color={lit ? accent : '#12100f'} transparent opacity={lit ? 0.92 : dim ? 0.6 : 0.78} toneMapped={false} depthWrite={false} depthTest={false} />
          </mesh>
          <Text position={[0, 0, 0.002]} renderOrder={21} fontSize={0.03} color={lit ? '#12100f' : dim ? '#a89c8e' : '#fff6ea'} anchorX="center" anchorY="middle" material-depthTest={false}>
            {label}
          </Text>
        </group>
      )}
    </group>
  );
}

// `model` ARRIVES AS A PROP, and that is the whole reason this works.
//
// It used to read interiorFloor.object — a module singleton MeshInterior fills
// in from an effect. But child effects run BEFORE parent effects, so on mount
// this ran first, found null, bailed, and had nothing in its dependencies that
// would ever change to make it try again. The mark was set, the object existed,
// and nothing glowed.
//
// The model comes out of a useMemo, so it is there during RENDER — passing it
// down makes it available before any effect runs and makes the effect re-run if
// the room is ever swapped underneath it.
export default function RoomMarkers({ roomId, model }) {
  const marks = useWorldStore((s) => s.marks);
  const seated = useWorldStore((s) => s.seated);
  const sitAt = useWorldStore((s) => s.sitAt);
  const standUp = useWorldStore((s) => s.standUp);
  const roomSeats = useWorldStore((s) => s.roomSeats);
  const setSeatNote = useWorldStore((s) => s.setSeatNote);
  const camera = useThree((s) => s.camera);
  const targets = useRef([]);
  const grp = useRef();
  const [actions, setActions] = useState([]);
  const [asking, setAsking] = useState(null);
  const askingRef = useRef(null);

  // Which colour a mark glows. The room's own accent by default, so a marker
  // reads as part of the place rather than as UI dropped on top of it.
  const accent = ROOMS[roomId]?.accent || '#ffffff';

  // ── A SHARED ROOM'S CHAIRS ARE ASKED FOR (2026-10-30) ─────────────────────
  // Khari: "This indoor room is a shared live experience (like the plaza), so
  // seats will actually be limited. Prevent sitting at one if it is taken, and
  // limit the time a user can sit (2 min)." In a `sharedSeats` room a chair
  // somebody else holds shows a dim "Taken" and no halo, and "Sit down" asks
  // the server first (worldSocket.seatTake): you sit only on a yes, with the
  // server's two minutes on the seat (`until`; SceneStaging counts them down
  // and stands you up). Nobody to ask = sit as before, still two minutes.
  // Every other room sits straight away, exactly as it always has.
  const shared = !!ROOMS[roomId]?.sharedSeats;
  const holds = shared && roomSeats?.zone === roomId ? roomSeats.seats : null;
  const takenByOther = (target) => {
    const h = holds?.[target];
    return !!h && !h.mine && h.until > Date.now();
  };
  const sit = async (act) => {
    if (!shared) { sitAt({ target: act.target, seat: act.pose, from: returnPose(camera) }); return; }
    if (askingRef.current) return;   // one ask at a time
    askingRef.current = act.target; setAsking(act.target);
    // The table this chair is at (rooms.js `tables`): a table of two is private and holds longer.
    const group = (ROOMS[roomId]?.tables || []).find((t) => (t.seats || []).includes(act.target))?.id || null;
    const r = await seatTake(roomId, act.target, group);
    askingRef.current = null; setAsking(null);
    if (!r.ok) {
      if (r.reason === 'taken') setSeatNote("Someone's sitting there");
      return;
    }
    // The answer came back to a different moment: you left, or sat elsewhere.
    const st = useWorldStore.getState();
    if (st.seated || st.interior !== roomId) { if (!r.offline) seatLeave(roomId, act.target); return; }
    sitAt({
      target: act.target, seat: act.pose, from: returnPose(camera),
      zone: roomId, until: r.until, held: !r.offline,
    });
  };

  const list = (qp ? [{ target: qp }] : marks) || [];
  const key = list.map((mk) => mk.target).join(',');

  useEffect(() => {
    const root = model || interiorFloor.object;
    if (!root || !list.length) {
      targets.current = [];
      setActions([]);
      // Silence is the worst possible answer to "why is nothing glowing".
      if (!root) console.warn('[mark] room model not ready yet');
      else console.log(`[mark] nothing marked in ${roomId} — try world.mark('wash_chair_1')`);
      return undefined;
    }

    const found = [];
    const acts = [];
    for (const mk of list) {
      const obj = root.getObjectByName(mk.target);
      if (!obj) {
        console.warn(`[mark] no object named "${mk.target}" in ${roomId}`
          + ' — units are named in scripts/make_salon.mjs (wash_chair_1, chair_1, hood_dryer_1, stool_1…)');
        continue;
      }
      // IF THE OBJECT HAS A SEAT, IT OFFERS A SIT. Nothing here knows what a
      // salon is — it asks the object what it affords and shows the action the
      // answer implies. A marked shelf would offer nothing and that is correct.
      // …or the mark brings its own pose: equipment that is not a seat but is
      // used like one (a treadmill, a bench press: GymFloor computes where your
      // eye goes and which way you look, and the seat system does the rest).
      const pose = mk.pose || seatPose(obj);
      if (pose) {
        const box = new THREE.Box3().setFromObject(obj);
        // WORLD → ROOM SPACE, properly. This layer is mounted inside the room's
        // anchor group, so its children are in the room's coordinates, while a
        // Box3 is always world. Subtracting the model's own `position` happened
        // to work only because the salon is a virtual room at the origin with no
        // rotation — it would put the prompt in the wrong place the moment a room
        // is sited at a building, nudged in Y, or turned.
        const at = new THREE.Vector3(
          (box.min.x + box.max.x) / 2,
          box.max.y + 0.26,
          (box.min.z + box.max.z) / 2,
        );
        // Converted against THIS component's own group rather than the shared
        // roomSpace singleton, for the same mount-order reason: a ref is assigned
        // at commit, before effects run, so it is always there when this needs it.
        if (grp.current) { grp.current.updateMatrixWorld(true); grp.current.worldToLocal(at); }
        const foot = new THREE.Vector3((box.min.x + box.max.x) / 2, box.min.y + 0.004, (box.min.z + box.max.z) / 2);
        if (grp.current) grp.current.worldToLocal(foot);
        const radius = Math.max(box.max.x - box.min.x, box.max.z - box.min.z) * 0.62;
        // The object's bounds in THIS group's space (a sited room is turned:
        // carry all eight corners across, not just the middle), for the hit area
        // that makes the object itself the button, and where its small label sits
        // on it — a little over half its height, in front of whatever it overlays.
        const lo = new THREE.Vector3(Infinity, Infinity, Infinity); const hi = new THREE.Vector3(-Infinity, -Infinity, -Infinity);
        for (const x of [box.min.x, box.max.x]) for (const y of [box.min.y, box.max.y]) for (const z of [box.min.z, box.max.z]) {
          const c = new THREE.Vector3(x, y, z);
          if (grp.current) grp.current.worldToLocal(c);
          lo.min(c); hi.max(c);
        }
        const hitBox = {
          center: [(lo.x + hi.x) / 2, (lo.y + hi.y) / 2, (lo.z + hi.z) / 2],
          size: [Math.max(0.05, hi.x - lo.x), Math.max(0.05, hi.y - lo.y), Math.max(0.05, hi.z - lo.z)],
          label: [(lo.x + hi.x) / 2, lo.y + (hi.y - lo.y) * 0.62, (lo.z + hi.z) / 2],
        };
        acts.push({ target: mk.target, pose, at: [at.x, at.y, at.z], foot: [foot.x, foot.y, foot.z], radius, box: hitBox });
      }
      obj.traverse((o) => {
        if (!o.isMesh || !o.material) return;
        // CLONE. The chair's leather is the same material instance as every
        // other leather surface in the room — glowing it in place would light up
        // the styling chairs too, which is the sort of bug that looks like the
        // marker "sometimes picks the wrong object".
        const mat = o.material.clone();
        mat.emissive = new THREE.Color(accent);
        mat.emissiveIntensity = MIN;
        found.push({ mesh: o, original: o.material, mat });
        o.material = mat;
      });
    }
    targets.current = found;
    setActions(acts);
    console.log(`[mark] ${found.length} mesh(es) marked in ${roomId}: ${list.map((mk) => mk.target).join(', ')}`
      + (acts.length ? ` · ${acts.length} seat action(s)` : ''));

    return () => {
      // Put the shared materials back and drop the clones, or leaving a room
      // with a mark up leaks a material per visit.
      for (const t of found) { t.mesh.material = t.original; t.mat.dispose(); }
      targets.current = [];
      setActions([]);
    };
  }, [key, roomId, accent, model]); // eslint-disable-line react-hooks/exhaustive-deps

  useFrame((state) => {
    if (!targets.current.length) return;
    // Half-cosine rather than a sine: it dwells at the bottom and rises
    // smoothly, which reads as breathing instead of a metronome.
    // Settles to steady while you are actually in the chair — the pulse is an
    // invitation, and it has done its job once you accept.
    const t = state.clock.elapsedTime * SPEED;
    const k = seated ? 0.25 : (1 - Math.cos(t)) / 2;
    const v = MIN + (MAX - MIN) * k;
    for (const x of targets.current) x.mat.emissiveIntensity = v;
  });

  return (
    <group ref={grp}>
      {actions.map((act) => (seated?.target !== act.target && takenByOther(act.target)
        ? null
        : <Halo key={`halo-${act.target}`} at={act.foot} r={act.radius} accent={accent} />))}
      {actions.map((act) => {
        const mine = seated?.target === act.target;
        // Nothing to press mid-move.
        if (seated && (!mine || seated.phase !== 'seated')) return null;
        const taken = !mine && takenByOther(act.target);
        return (
          <Action
            key={act.target}
            box={act.box}
            accent={accent}
            dim={taken || asking === act.target}
            mine={mine}
            asking={asking === act.target}
            label={mine ? 'Stand up' : taken ? 'Taken' : asking === act.target ? '…' : act.pose.label}
            onPick={() => {
              if (mine) { standUp(); return; }
              sit(act);
            }}
          />
        );
      })}
    </group>
  );
}
